import type { OwnShipState, TargetState } from "@radar-sim/schema";
import { computeCPA } from "@radar-sim/engine";

/** Phase a MARPA target is in. Solution build takes time. */
export type MarpaPhase =
  | "acquiring" // 0-10s: target detected, no vector yet
  | "building" // 10-30s+: weak vector, dashed
  | "tracked" // 30-90s+: full vector
  | "lost"; // target visibility dropped during tracking

export interface MarpaTrack {
  vesselId: string;
  /** simSeconds when the user tapped to acquire. */
  acquiredAtSimSeconds: number;
  /** Number of full seconds elapsed since acquisition. */
  elapsedSeconds: number;
  phase: MarpaPhase;
  /** Range from own to target at current snapshot (nm). */
  rangeNm: number;
  /** True bearing from own to target at current snapshot. */
  bearingDeg: number;
  /** Closest point of approach (nm), based on current vectors. */
  cpaNm: number;
  /** Time to CPA (minutes). Negative = opening. */
  tcpaMin: number;
}

/** Time after acquisition before the vector starts to render at all. */
const SOLUTION_INITIAL_SECONDS = 10;
/** Time after acquisition until the solution is "good" (full-confidence vector). */
export const SOLUTION_BUILD_SECONDS = 60;

/**
 * Per-client MARPA tracker. Holds acquired-target state across snapshots
 * and computes CPA/TCPA on each tick. Lives in the consumer (browser) — the
 * source has no notion of MARPA other than acquire/drop commands for the
 * "instructor shared cursor" use case.
 */
export class MarpaTracker {
  private readonly tracks = new Map<string, { acquiredAtSimSeconds: number }>();
  private latest = new Map<string, MarpaTrack>();
  private readonly listeners = new Set<
    (tracks: ReadonlyArray<MarpaTrack>) => void
  >();

  acquire(vesselId: string, simSeconds: number): void {
    if (!this.tracks.has(vesselId)) {
      this.tracks.set(vesselId, { acquiredAtSimSeconds: simSeconds });
      this.emit();
    }
  }

  drop(vesselId: string): void {
    if (this.tracks.delete(vesselId)) {
      this.latest.delete(vesselId);
      this.emit();
    }
  }

  isAcquired(vesselId: string): boolean {
    return this.tracks.has(vesselId);
  }

  list(): ReadonlyArray<MarpaTrack> {
    return [...this.latest.values()];
  }

  /**
   * Subscribe to track-list updates. Called after every `update()` and
   * after `acquire`/`drop`. The AIS readout panel uses this to keep its
   * displayed CPA/TCPA fresh without polling.
   */
  subscribe(
    listener: (tracks: ReadonlyArray<MarpaTrack>) => void,
  ): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    const list = this.list();
    for (const l of this.listeners) l(list);
  }

  /**
   * Update tracks against the latest snapshot. Returns the current track set.
   * Targets that have disappeared from the snapshot are marked as `lost`.
   */
  update(
    simSeconds: number,
    ownShip: OwnShipState,
    targets: ReadonlyArray<TargetState>,
  ): ReadonlyArray<MarpaTrack> {
    const next = new Map<string, MarpaTrack>();
    for (const [id, t] of this.tracks) {
      const target = targets.find((tg) => tg.id === id);
      const elapsed = simSeconds - t.acquiredAtSimSeconds;
      if (target === undefined || !target.visible) {
        const previous = this.latest.get(id);
        if (previous !== undefined) {
          next.set(id, { ...previous, elapsedSeconds: elapsed, phase: "lost" });
        }
        continue;
      }
      const cpa = computeCPA(
        { position: ownShip.position, cog: ownShip.cog, sog: ownShip.sog },
        { position: target.position, cog: target.cog, sog: target.sog },
      );
      const phase: MarpaPhase =
        elapsed < SOLUTION_INITIAL_SECONDS
          ? "acquiring"
          : elapsed < SOLUTION_BUILD_SECONDS
            ? "building"
            : "tracked";
      next.set(id, {
        vesselId: id,
        acquiredAtSimSeconds: t.acquiredAtSimSeconds,
        elapsedSeconds: elapsed,
        phase,
        rangeNm: cpa.rangeNm,
        bearingDeg: cpa.bearingDeg,
        cpaNm: cpa.cpaNm,
        tcpaMin: cpa.tcpaMin,
      });
    }
    this.latest = next;
    this.emit();
    return [...next.values()];
  }
}
